import React, { useState } from "react";
import { DialogContent, DialogFooter, DialogFullHeader } from "@atoms/Dialog";
import Title from "@atoms/Title/Title";
import Section from "@atoms/Section/Section";
import Text from "@atoms/Text/Text";
import Button from "@atoms/Button/Button";
import InputText from "@atoms/InputText/InputText";
import Textarea from "@atoms/Textarea/Textarea";
import SelectInput from "@atoms/SelectInput/SelectInput";
import "./ProviderNewsletterFormModal.scss";
import { useTranslation } from "react-i18next";
import addToMailchimp from "gatsby-plugin-mailchimp";
import { useStaticQuery, graphql } from "gatsby";
import Img from "gatsby-image";

const emailPattern = new RegExp(
	/^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i,
);

const mailchimpEndPoint =
	"https://aepsy.us17.list-manage.com/subscribe/post?u=61cbde422266875a1eaf1b26b&amp;id=8eb558d9bb";

interface Props {
	onClose: VoidFunction;
	defaultSpecialInterest: string;
}

const ProviderNewsletterFormModal: React.FC<Props> = ({
	onClose,
	defaultSpecialInterest,
}) => {
	const data = useStaticQuery(graphql`
        query {
            image: file(
                relativePath: { eq: "employer-recommendation.png" }
            ) {
                childImageSharp {
                    fluid(maxWidth: 150) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
        }
    `);

	const { t } = useTranslation();
	const [success, setSuccess] = useState(false);
	const [providerTitle, setProviderTitle] = useState("");
	const [email, setEmail] = useState("");
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		specialInterest: defaultSpecialInterest,
	});
	const [emailError, setEmailError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const onChange = (event: any) => {
		const { name, value } = event.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const onEmailChange = (event: any) => {
		setEmail(event.target.value);
	};

	const onEmailBlur = (event: any) => {
		setEmailError(!emailPattern.test(event.target.value));
	};

	const onProviderTitleChange = (event: any) => {
		setProviderTitle(event.target.value);
	};

	const onSubmit = async (e: any) => {
		e.preventDefault();
		setIsLoading(true);

		try {
			const res = await addToMailchimp(
				email,
				{
					mmerge4: formData?.firstName,
					mmerge5: formData?.lastName,
					mmerge2: providerTitle,
					mmerge3: formData?.specialInterest,
				},
				`${mailchimpEndPoint}&SIGNUP=for-psychologist`,
			);
			setIsLoading(false);
			if (res.result === "success") {
				setSuccess(true);
			} else {
				// show failure
			}
		} catch (e) {
			setIsLoading(false);
			console.log(e?.message);
		}
	};

	if (success) {
		return (
			<>
				<DialogContent>
					<div className="ProviderNewsletterFormModal--image">
						<Img fluid={data?.image?.childImageSharp?.fluid} />
					</div>
					<Section spacing="xs">
						<Title size="ml" align="center">
							{t("provider.newsletter.success.title")}
						</Title>
						<Text size="s" align="center">
							{t("provider.newsletter.success.subtitle")}
						</Text>
					</Section>
				</DialogContent>
				<DialogFooter>
					<Button
						label={t("generic.close")}
						onClick={onClose}
						theme="pink-gradient"
					/>
				</DialogFooter>
			</>
		);
	}

	return (
		<>
			<DialogFullHeader hasDivider={false} onCloseButtonClick={onClose} />
			<DialogContent hasFooter>
				<div className="ProviderNewsletterFormModal--image">
					<Img fluid={data?.image?.childImageSharp?.fluid} />
				</div>
				<Section spacing="xs">
					<Title size="ml" align="center">
						{t("provider.newsletter.title")}
					</Title>
					<Text size="s" align="center">
						{t("provider.newsletter.subtitle")}
					</Text>
				</Section>
				<form>
					<div className="ProviderNewsletterForm">
						<div className="ProviderNewsletterForm--field">
							<InputText
								label={t("generic.first.name")}
								id="firstName"
								onChange={onChange}
							/>
						</div>
						<div className="ProviderNewsletterForm--field">
							<InputText
								label={t("generic.last.name")}
								id="lastName"
								onChange={onChange}
							/>
						</div>
						<div className="ProviderNewsletterForm--field">
							<InputText
								type="email"
								label={t("generic.email")}
								name="email"
								onChange={onEmailChange}
								onBlur={onEmailBlur}
								helperText={emailError ? t("error.email.invalid") : ""}
								autoComplete="email"
								error={emailError}
							/>
						</div>
						<div className="ProviderNewsletterForm--field">
							<SelectInput
								items={[
									{
										label: t("provider.title.psychologist"),
										value: "Psychologist",
									},
									{
										label: t("provider.title.psychotherapist"),
										value: "Psychotherapist",
									},
									{
										label: t("provider.title.psychotherapist.in.training"),
										value: "Psychotherapist in training",
									},
								]}
								label={t("providerContent.provider.title")}
								onChange={onProviderTitleChange}
							/>
						</div>
						<div className="ProviderNewsletterForm--label">
							<Title size="s" noMargin>
								{t("provider.newsletter.special.interest")}
							</Title>
						</div>
						<div className="ProviderNewsletterForm--field">
							<Textarea
								placeholder={t("generic.placeholder.optional")}
								onChange={onChange}
								name="specialInterest"
								rows={2}
								value={formData?.specialInterest}
							/>
						</div>
					</div>
				</form>
			</DialogContent>
			<DialogFooter>
				<Button
					onClick={onSubmit}
					label={t("generic.send")}
					align="center"
					theme="pink-gradient"
					disabled={
						emailError ||
						formData?.firstName?.length === 0 ||
						formData?.lastName?.length === 0 ||
						email?.length === 0 ||
						providerTitle?.length === 0
					}
					isLoading={isLoading}
				/>
				<Button
					variant="naked"
					onClick={onClose}
					label={t("generic.close")}
					align="center"
					theme="highlighted"
				/>
			</DialogFooter>
		</>
	);
};

export default ProviderNewsletterFormModal;
